import {
  OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
  type OemAdditionalTestFitReservation,
  type OemElectricalMachineProduct,
  type OemGenerationElectricalData,
  type OemGenerationPerformanceCase,
  type OemGenerationProduct,
  type OemGenerationSourceRegister,
  type OemPhysicalReference,
} from '../types/oemGenerationCatalog';

const RETRIEVED_AT = '2026-07-20';

const STANDARD_TEST_FIT_RESERVATIONS = [
  'maintenance-access',
  'fire-access',
  'air-inlet-and-exhaust',
  'fuel-conditioning',
  'cooling',
  'transformer-and-switchgear',
  'acoustic-treatment',
  'roads-and-crane-access',
  'stormwater-and-grading',
  'construction-laydown',
] as const satisfies readonly OemAdditionalTestFitReservation[];

const CHP_TEST_FIT_RESERVATIONS = [
  ...STANDARD_TEST_FIT_RESERVATIONS,
  'heat-recovery',
] as const satisfies readonly OemAdditionalTestFitReservation[];

type PerformanceSeed = Pick<
  OemGenerationPerformanceCase,
  | 'id'
  | 'label'
  | 'frequencyHz'
  | 'duty'
  | 'fuel'
  | 'electricalOutputKw'
  | 'ratingScope'
  | 'referenceConditions'
  | 'sourceIds'
> & Partial<Omit<OemGenerationPerformanceCase,
  | 'id'
  | 'label'
  | 'frequencyHz'
  | 'duty'
  | 'fuel'
  | 'electricalOutputKw'
  | 'ratingScope'
  | 'referenceConditions'
  | 'sourceIds'
>>;

function performance(seed: PerformanceSeed): OemGenerationPerformanceCase {
  return {
    powerFactor: null,
    electricalEfficiencyPct: null,
    heatRate: null,
    fuelInputKw: null,
    exhaustMassFlowKgS: null,
    exhaustTemperatureC: null,
    steamProductionKgPerHour: null,
    minimumLoadPct: null,
    startTimeMinutes: null,
    rampRateMwPerMinute: null,
    ...seed,
  };
}

type PhysicalSeed = Pick<
  OemPhysicalReference,
  | 'id'
  | 'label'
  | 'scope'
  | 'lengthM'
  | 'widthM'
  | 'configurationScope'
  | 'coverageScope'
  | 'includes'
  | 'sourceIds'
  | 'limitation'
> & Partial<Omit<OemPhysicalReference,
  | 'id'
  | 'label'
  | 'scope'
  | 'lengthM'
  | 'widthM'
  | 'configurationScope'
  | 'coverageScope'
  | 'includes'
  | 'sourceIds'
  | 'limitation'
>>;

function physical(seed: PhysicalSeed): OemPhysicalReference {
  return {
    heightM: null,
    massTonnes: null,
    quantity: 1,
    maintenanceClearanceIncluded: null,
    fireAccessIncluded: null,
    ...seed,
  };
}

function limitedElectrical(
  sourceIds: readonly string[],
  notes = 'Public OEM material does not provide a complete study-grade alternator, AVR, governor, RMS, or EMT model.',
  overrides: Partial<OemGenerationElectricalData> = {},
): OemGenerationElectricalData {
  return {
    alternatorManufacturer: null,
    alternatorModel: null,
    ratedKva: null,
    nominalVoltagesKv: [],
    ratedPowerFactor: null,
    motorStartingKva: null,
    rotorInertiaKgM2: null,
    reactances: null,
    timeConstants: null,
    shortCircuitDecrementCurvePublished: false,
    avrModelPublished: false,
    governorModelPublished: false,
    validatedRmsModelPublished: false,
    validatedEmtModelPublished: false,
    sourceIds,
    notes,
    ...overrides,
  };
}

function product(
  seed: Omit<OemGenerationProduct, 'schemaVersion'>,
): OemGenerationProduct {
  return {
    schemaVersion: OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
    ...seed,
  };
}

type EvidenceProductSeed = Pick<
  OemGenerationProduct,
  | 'id'
  | 'manufacturer'
  | 'model'
  | 'variant'
  | 'technology'
  | 'packageScope'
  | 'fuels'
  | 'identitySourceIds'
  | 'preferredPerformanceCaseId'
  | 'performanceCases'
> & Partial<Pick<
  OemGenerationProduct,
  | 'productStatus'
  | 'partLoadPoints'
  | 'heatRecoveryCases'
  | 'electrical'
  | 'physicalReferences'
  | 'clearances'
  | 'additionalTestFitReservations'
  | 'simulationCatalogId'
  | 'dataGaps'
  | 'notes'
>>;

function evidenceProduct(seed: EvidenceProductSeed): OemGenerationProduct {
  return product({
    productStatus: 'current',
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(seed.identitySourceIds),
    physicalReferences: [],
    clearances: [],
    additionalTestFitReservations: seed.packageScope === 'combined-heat-and-power-package'
      ? CHP_TEST_FIT_RESERVATIONS
      : STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: [
      'Project-specific price and lead time',
      'Part-load and ambient correction curves',
      'Installed GA with maintenance and fire access',
      'Study-grade generator, AVR, governor, RMS, and EMT models',
    ],
    ...seed,
  });
}

const sources = [
  {
    id: 'kawasaki-gpb300d-global',
    publisher: 'Kawasaki Heavy Industries',
    title: 'Gas Turbine Cogeneration Systems product lineup',
    url: 'https://global.kawasaki.com/en/energy/equipment/gas_turbines/cogeneration.html',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
    notes: 'Current global rating table and package dimensions.',
  },
  {
    id: 'kawasaki-gpb300d-eu',
    publisher: 'Kawasaki Gas Turbine Europe',
    title: 'GPB300D product data sheet',
    url: 'https://www.kawasaki-gasturbine.de/files/GPB300D.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'Layout revision 2, 2021-01-27',
    notes: 'Contains an arrangement with turbine and generator maintenance areas; fire access is not established.',
  },
  {
    id: 'kawasaki-gtgs-guide',
    publisher: 'Kawasaki Heavy Industries',
    title: 'Kawasaki Gas Turbine Generator Sets',
    url: 'https://global.kawasaki.com/assets/pdf/en/gtgs.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-guide',
    access: 'public',
    notes: 'Typical cogeneration layouts are conceptual and are not project general arrangements.',
  },
  {
    id: 'solar-pgm70-2023',
    publisher: 'Solar Turbines',
    title: 'PGM70 Power Generation Module',
    url: 'https://s7d2.scene7.com/is/content/Caterpillar/CM20201103-98535-2df8a',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'DSPGM70/0123/EO',
  },
  {
    id: 'solar-taurus70-current',
    publisher: 'Solar Turbines',
    title: 'Taurus 70 Gas Turbine Generator Set',
    url: 'https://www.solarturbines.com/en_US/products/power-generation-packages/taurus-70.html',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
    notes: 'Current web values differ from the versioned 2023 PGM sheet and are retained as a separate case.',
  },
  {
    id: 'baker-novalt12-2022',
    publisher: 'Baker Hughes',
    title: 'NovaLT12 Overview',
    url: 'https://www.bakerhughes.com/sites/bakerhughes/files/2022-06/novalt12_overview.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
  },
  {
    id: 'baker-novalt12-current',
    publisher: 'Baker Hughes',
    title: 'NovaLT12 gas turbine product page',
    url: 'https://www.bakerhughes.com/gas-turbines/novalt-technology/novalt12',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
  },
  {
    id: 'mhi-h25-current',
    publisher: 'Mitsubishi Power',
    title: 'H-25 gas turbine',
    url: 'https://power.mhi.com/regions/amer/products/gas-turbines/h25',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
  },
  {
    id: 'siemens-sgt800-current',
    publisher: 'Siemens Energy',
    title: 'SGT-800 industrial gas turbine',
    url: 'https://www.siemens-energy.com/global/en/home/products-services/product/sgt-800.html',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
  },
  {
    id: 'siemens-sgt800-poster-2025',
    publisher: 'Siemens Energy',
    title: 'SGT-800 metric technical data poster',
    url: 'https://assets.siemens-energy.com/dam/84313f36-ce72-41ba-a556-b29f0117e69f/Poster-SGT-800_ENG_A_preview_250304-Kopie-pdf_Original%20file.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: '2025-03-04',
  },
  {
    id: 'rehlko-kg125-g4289',
    publisher: 'Rehlko',
    title: 'KG125 Industrial Gaseous Generator Set',
    url: 'https://resources.kohler.com/power/kohler/industrial/pdf/g4289.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'G4-289, 8/23g',
  },
  {
    id: 'generac-sg175-2024',
    publisher: 'Generac',
    title: 'SG175 Industrial Gaseous Generator Set',
    url: 'https://www.generac.com/globalassets/products/business/stationary-generators/gaseous-industrial-generators/spec-sheets/sg175-175kw-industrial-gaseous-generator-specsheet.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: '10000038894 Rev D, 2024-01-24',
  },
  {
    id: 'innio-j320-2025',
    publisher: 'INNIO Jenbacher',
    title: 'J320 technical specification',
    url: 'https://innio.com/images/medias/files/163/ijb_ets_t3_a4_en_2025_screen_ijb-125003-en.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'IJB-125003-EN, 2025',
  },
  {
    id: 'cummins-c1100n6c-d6204',
    publisher: 'Cummins',
    title: 'C1100N6C Gas Generator Set Data Sheet',
    url: 'https://www.cummins.com/sites/default/files/2020-02/C1100N6C%20-%20D-6204.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'D-6204, 08/17',
  },
  {
    id: 'cat-g3520h-lehe1267',
    publisher: 'Caterpillar',
    title: 'G3520H 50 Hz Gas Generator Set Technical Data',
    url: 'https://emc.cat.com/pubdirect.ashx?media_string_id=LEHE1267-',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'LEHE1267-05, 06/23',
  },
  {
    id: 'cat-g3520h-current',
    publisher: 'Caterpillar',
    title: 'G3520H Gas Generator Set product page',
    url: 'https://www.cat.com/en_US/products/new/power-systems/electric-power/gas-generator-sets/1000003143.html',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
    notes: 'Portal dimensions are configuration-aggregate; its displayed metric and imperial masses do not convert consistently.',
  },
  {
    id: 'cat-3512b-lehe1757',
    publisher: 'Caterpillar',
    title: '3512B 50 Hz Diesel Generator Set Technical Data',
    url: 'https://emc.cat.com/pubdirect.ashx?media_string_id=LEHE1757-03.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'LEHE1757-03, 12/22',
  },
  {
    id: 'mwm-tcg3020-2024',
    publisher: 'MWM',
    title: 'TCG 3020 Gas Engine Product Brochure',
    url: 'https://www.mwm.net/files/upload/mwm/issuu/CES24005-Update-Produktbroschuere-TCG-3020_210x297mm_EN_10_0_RZ_sm_screen_einzel.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-guide',
    access: 'public',
    revision: '03-24/EN',
  },
  {
    id: 'mtu-ds3000-2026',
    publisher: 'Rolls-Royce mtu',
    title: '20V4000 DS3000 Generator Set Specification',
    url: 'https://www.mtu-solutions.com/content/dam/mtu/products/power-generation/powergeneration-product-list-latest/231161_PG_Spec_20V4000DS3000G04_2790kW_3F_T2_SCAQMD_60Hz_50C.pdf/_jcr_content/renditions/original./231161_PG_Spec_20V4000DS3000G04_2790kW_3F_T2_SCAQMD_60Hz_50C.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: '231161, 2026-03',
  },
  {
    id: 'wartsila-34sg-2023',
    publisher: 'Wärtsilä',
    title: 'Wärtsilä 34SG Product Leaflet',
    url: 'https://www.wartsila.com/docs/default-source/energy-docs/technology-products/product-leaflets/wartsila-34sg.pdf?sfvrsn=c3c5a345_20',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: '09/2023',
  },
  {
    id: 'wartsila-34sg-current',
    publisher: 'Wärtsilä',
    title: 'Wärtsilä 34SG gas engine product page',
    url: 'https://www.wartsila.com/energy/solutions/engine-power-plants/wartsila-34sg-gas-engine',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
  },
  {
    id: 'bergen-b3645v-2026',
    publisher: 'Bergen Engines',
    title: 'B36:45V Engine Range Fact Sheet',
    url: 'https://www.bergenengines.com/wp-content/uploads/2024/06/Bergen-Engines-B3645V-Fact-Sheet-1.pdf',
    retrievedAt: RETRIEVED_AT,
    kind: 'datasheet',
    access: 'public',
    revision: 'Version 1/2026',
  },
  {
    id: 'bergen-b3645v-current',
    publisher: 'Bergen Engines',
    title: 'Bergen B36:45V product page',
    url: 'https://www.bergenengines.com/engines/bergen-b3645v/',
    retrievedAt: RETRIEVED_AT,
    kind: 'product-page',
    access: 'public',
    notes: 'Current page values are not merged with the versioned January 2026 fact sheet.',
  },
  {
    id: 'capstone-c65-current', publisher: 'Capstone Energy', title: 'C65 microturbine',
    url: 'https://www.capstoneenergyplus.com/products/capstone-microturbines/c65', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'capstone-c200s-current', publisher: 'Capstone Energy', title: 'C200S microturbine',
    url: 'https://www.capstoneenergyplus.com/products/capstone-microturbines/c200s', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'capstone-c600s-current', publisher: 'Capstone Energy', title: 'C600S microturbine',
    url: 'https://www.capstoneenergyplus.com/products/capstone-microturbines/c600s', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'capstone-c800s-current', publisher: 'Capstone Energy', title: 'C800S microturbine',
    url: 'https://www.capstoneenergyplus.com/products/capstone-microturbines/c800s', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'capstone-c1000s-current', publisher: 'Capstone Energy', title: 'C1000S microturbine',
    url: 'https://www.capstoneenergyplus.com/products/capstone-microturbines/c1000s', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'ge-7ha-power-plants-2025', publisher: 'GE Vernova', title: '7HA power plants fact sheet',
    url: 'https://www.gevernova.com/content/dam/gepower/global/en_US/documents/gas/gas-turbines/heavy-duty-products-specs/7ha-power-plants-fact-sheet.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public', revision: '2025',
  },
  {
    id: 'ge-lm6000-current', publisher: 'GE Vernova', title: 'LM6000 aeroderivative gas turbine',
    url: 'https://www.gevernova.com/gas-power/products/gas-turbines/lm6000', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'ge-gas-power-catalog-2025', publisher: 'GE Vernova', title: 'Gas Power Products and Services catalog',
    url: 'https://www.gevernova.com/content/dam/gepower-new/global/en_US/downloads/gas-new-site/catalog/GEA35241-2025-GE-Vernova-Gas-Power-Catalog.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'portfolio', access: 'public', revision: 'GEA35241, 2025',
  },
  {
    id: 'siemens-scc6-5000f-2025', publisher: 'Siemens Energy', title: 'Combined-cycle technical data — SCC6-5000F',
    url: 'https://assets.siemens-energy.com/dam/8224a82f-9ed8-475e-b946-b26c0169f8fd/brochure-combined-cycle-technical-Data-250121b-pdf_Original%20file.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public', revision: '2025-01-21',
  },
  {
    id: 'ansaldo-gt36-2025', publisher: 'Ansaldo Energia', title: 'GT36 gas turbine',
    url: 'https://www.ansaldoenergia.com/fileadmin/Brochure/2025/GT36.pdf', retrievedAt: RETRIEVED_AT,
    kind: 'datasheet', access: 'public', revision: '2025',
  },
  {
    id: 'everllence-3544g-2025', publisher: 'Everllence', title: '35/44G gas engine factsheet',
    url: 'https://www.everllence.com/docs/default-source/energy-factsheets/evr-000011en_l3_v35-44g_preview.pdf?sfvrsn=78d30ba6_4',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public', revision: 'EVR000011EN-250600',
  },
  {
    id: 'guascor-h-series-2023', publisher: 'Guascor Energy', title: 'H-series gas engines and gensets — natural gas',
    url: 'https://guascor-energy.com/wp-content/uploads/2023/02/6006_H-series-gas-engines-and-gensets_natural-gas_en.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public',
  },
  {
    id: 'wartsila-50sg-current', publisher: 'Wärtsilä', title: 'Wärtsilä 50SG product leaflet',
    url: 'https://www.wartsila.com/docs/default-source/energy-docs/technology-products/product-leaflets/wartsila-50sg.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public',
  },
  {
    id: 'cat-g3520-fast-response-current', publisher: 'Caterpillar', title: 'Cat G3520 gas generators for critical power applications',
    url: 'https://www.cat.com/en_US/campaigns/npi/cat-g3520-gas-generators.html', retrievedAt: RETRIEVED_AT,
    kind: 'product-page', access: 'public',
  },
  {
    id: 'stamford-s5l1d-h4-2025', publisher: 'STAMFORD | AvK', title: 'S5L1D-H4 winding 311 technical data',
    url: 'https://www.stamford-avk.com/sites/stamfordavk/files/ES10461983_S5L1D-H4-B-W311.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public', revision: 'Rev B, 2025-05-20',
  },
  {
    id: 'avk-dig140k6-4160', publisher: 'STAMFORD | AvK', title: 'DIG140k/6 60 Hz 4.16 kV technical data',
    url: 'https://www.stamford-avk.com/sites/stamfordavk/files/dig140k_6_60_4160.pdf',
    retrievedAt: RETRIEVED_AT, kind: 'datasheet', access: 'public', revision: 'Rev B, 2013-10-17',
  },
  {
    id: 'leroy-lsa493-2020', publisher: 'Leroy-Somer / Nidec Power', title: 'LSA 49.3 S4A technical specification',
    url: 'https://www.leroy-somer.com/documentation_pdf/5909_en.pdf', retrievedAt: RETRIEVED_AT,
    kind: 'datasheet', access: 'public', revision: '5909en-2020.01/a',
  },
] as const satisfies OemGenerationSourceRegister['sources'];

const products = [
  product({
    id: 'kawasaki-gpb300d-natural-gas',
    manufacturer: 'Kawasaki Heavy Industries',
    model: 'GPB300D',
    variant: 'Natural-gas cogeneration package, global rating',
    technology: 'industrial-gas-turbine',
    packageScope: 'combined-heat-and-power-package',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['kawasaki-gpb300d-global', 'kawasaki-gpb300d-eu'],
    preferredPerformanceCaseId: 'global-iso',
    performanceCases: [performance({
      id: 'global-iso',
      label: 'Global OEM reference rating',
      frequencyHz: [],
      duty: 'iso-rating',
      fuel: 'Natural gas, LHV 35.9 MJ/Nm³',
      electricalOutputKw: 32_360,
      ratingScope: 'generator-terminals',
      heatRate: { valueKjPerKwh: 9_110, heatingValueBasis: 'LHV' },
      exhaustMassFlowKgS: 87.7222,
      exhaustTemperatureC: 519,
      steamProductionKgPerHour: 54_100,
      referenceConditions: '0 m elevation; 15°C; inlet loss 0.98 kPa; exhaust loss 3.43 kPa; natural-gas LHV 35.9 MJ/Nm³.',
      sourceIds: ['kawasaki-gpb300d-global'],
      notes: 'Published total thermal efficiency is 84.3%. Frequency is not stated in the global table.',
    })],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['kawasaki-gpb300d-global']),
    physicalReferences: [
      physical({
        id: 'global-package',
        label: 'Global packaged generator set',
        scope: 'packaged-module',
        lengthM: 14.2,
        widthM: 4.5,
        heightM: 5.3,
        massTonnes: 155,
        configurationScope: 'exact-product',
        coverageScope: 'per-product',
        includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure'],
        maintenanceClearanceIncluded: false,
        fireAccessIncluded: false,
        sourceIds: ['kawasaki-gpb300d-global'],
        limitation: 'Package only; excludes the full inlet/stack/HRSG, balance of plant, maintenance pull space, crane access, and fire lanes.',
      }),
      physical({
        id: 'eu-arrangement',
        label: 'European outdoor arrangement with marked maintenance areas',
        scope: 'installed-operating-envelope',
        lengthM: 24.4,
        widthM: 13.498,
        heightM: 14.9,
        configurationScope: 'exact-product',
        coverageScope: 'per-product',
        includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure', 'air-inlet', 'exhaust'],
        maintenanceClearanceIncluded: true,
        fireAccessIncluded: false,
        sourceIds: ['kawasaki-gpb300d-eu'],
        limitation: 'Published arrangement marks turbine and generator maintenance areas, but does not establish project fire access, fuel conditioning, HRSG, cooling, electrical yard, civil works, or external crane/road reservations.',
      }),
      physical({
        id: 'typical-chp-layout',
        label: 'Typical OEM cogeneration layout',
        scope: 'typical-plant-layout',
        lengthM: 50,
        widthM: 40,
        configurationScope: 'product-family',
        coverageScope: 'whole-plant',
        includes: ['prime-mover', 'generator', 'controls', 'air-inlet', 'exhaust', 'fuel-conditioning', 'cooling', 'heat-recovery', 'balance-of-plant'],
        sourceIds: ['kawasaki-gtgs-guide'],
        limitation: 'Conceptual OEM layout only. Equipment selection, maintenance access, fire separation, roads, crane access, transformer/switchgear, grading, stormwater, and construction laydown require a project GA.',
      }),
    ],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Frequency-specific rating', 'Part-load heat-rate curve', 'Numeric start and ramp data', 'Project GA with fire and external service access', 'Study-grade generator and controls models'],
  }),
  product({
    id: 'solar-pgm70-taurus70-2023',
    manufacturer: 'Solar Turbines',
    model: 'PGM70 / Taurus 70',
    variant: 'Compact-filter power generation module, 2023 data',
    technology: 'industrial-gas-turbine',
    packageScope: 'packaged-module',
    productStatus: 'current',
    fuels: ['natural gas', 'hydrogen blend up to published limit'],
    identitySourceIds: ['solar-pgm70-2023', 'solar-taurus70-current'],
    preferredPerformanceCaseId: 'pgm70-2023-iso',
    performanceCases: [
      performance({
        id: 'pgm70-2023-iso',
        label: 'PGM70 2023 ISO case',
        frequencyHz: [],
        duty: 'iso-rating',
        fuel: 'Natural gas, LHV 35 MJ/Nm³',
        electricalOutputKw: 8_180,
        ratingScope: 'generator-terminals',
        electricalEfficiencyPct: 34.4,
        heatRate: { valueKjPerKwh: 10_470, heatingValueBasis: 'LHV' },
        exhaustMassFlowKgS: 26.8542,
        exhaustTemperatureC: 520,
        referenceConditions: 'ISO; 15°C; sea level; 60% RH; no inlet, exhaust, or accessory losses.',
        sourceIds: ['solar-pgm70-2023'],
        notes: 'The sheet’s 74.4 t/h unfired saturated-steam value is quarantined as physically inconsistent with the published exhaust stream pending OEM confirmation.',
      }),
      performance({
        id: 'taurus70-current-web',
        label: 'Current Taurus 70 web case',
        frequencyHz: [],
        duty: 'iso-rating',
        fuel: 'Natural gas',
        electricalOutputKw: 8_180,
        ratingScope: 'generator-terminals',
        heatRate: { valueKjPerKwh: 10_505, heatingValueBasis: 'LHV' },
        exhaustMassFlowKgS: 26.8819,
        exhaustTemperatureC: 505,
        referenceConditions: 'Current OEM web reference conditions; retained separately from DSPGM70/0123/EO.',
        sourceIds: ['solar-taurus70-current'],
        notes: 'Version-specific performance conflict; do not combine with the 2023 case.',
      }),
    ],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['solar-pgm70-2023']),
    physicalReferences: [physical({
      id: 'compact-filter-module',
      label: 'PGM70 compact-filter module',
      scope: 'packaged-module',
      lengthM: 14.7,
      widthM: 2.9,
      heightM: 7.7,
      massTonnes: 80.35,
      configurationScope: 'exact-product',
      coverageScope: 'per-product',
      includes: ['prime-mover', 'generator', 'gearbox', 'controls', 'acoustic-enclosure', 'air-inlet'],
      maintenanceClearanceIncluded: false,
      fireAccessIncluded: false,
      sourceIds: ['solar-pgm70-2023'],
      limitation: 'Length and width are the preassembled module; 7.7 m is the minimum total height with compact filter (the turbine enclosure is 3.5 m). Not a complete power island or site footprint.',
    })],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Frequency and duty classification', 'Part-load curve', 'Numeric start/ramp/minimum load', 'Installed GA and service clearances', 'Confirmed heat-recovery duty', 'Study-grade electrical models'],
  }),
  product({
    id: 'baker-hughes-novalt12',
    manufacturer: 'Baker Hughes',
    model: 'NovaLT12',
    variant: 'Power-generation package, 2022 reference',
    technology: 'industrial-gas-turbine',
    packageScope: 'packaged-module',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['baker-novalt12-2022', 'baker-novalt12-current'],
    preferredPerformanceCaseId: 'iso-power-generation',
    performanceCases: [performance({
      id: 'iso-power-generation',
      label: 'ISO power-generation case',
      frequencyHz: [50, 60],
      duty: 'iso-rating',
      fuel: 'Natural gas',
      electricalOutputKw: 12_500,
      ratingScope: 'generator-terminals',
      electricalEfficiencyPct: 35.3,
      exhaustTemperatureC: 496,
      referenceConditions: 'ISO; 15°C; sea level; 60% RH; no inlet or exhaust losses.',
      sourceIds: ['baker-novalt12-2022', 'baker-novalt12-current'],
    })],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['baker-novalt12-2022'], '50/60 Hz capability is public; study-grade alternator and control models are not.'),
    physicalReferences: [physical({
      id: 'power-generation-package-2022',
      label: '2022 power-generation package',
      scope: 'packaged-module',
      lengthM: 14.3,
      widthM: 2.5,
      heightM: 6.4,
      massTonnes: 113,
      configurationScope: 'exact-product',
      coverageScope: 'per-product',
      includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure'],
      maintenanceClearanceIncluded: false,
      fireAccessIncluded: false,
      sourceIds: ['baker-novalt12-2022'],
      limitation: 'Published dimension scope is ambiguous regarding driven equipment. Current web material reports a conflicting nominal 3.5 m height; retain this revision and require the selected-package GA.',
    })],
    clearances: [],
    additionalTestFitReservations: STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Heating-value basis and heat rate', 'Exhaust mass flow', 'Part-load curve', 'Numeric start/ramp/minimum load', 'Reconciled package height and project GA', 'Study-grade electrical models'],
    notes: 'Do not overwrite the 2022 6.4 m height with the current-page 3.5 m statement; configuration scope is unresolved.',
  }),
  product({
    id: 'mitsubishi-power-h25',
    manufacturer: 'Mitsubishi Power',
    model: 'H-25',
    variant: 'Simple-cycle ISO base rating',
    technology: 'industrial-gas-turbine',
    packageScope: 'engine-only',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['mhi-h25-current'],
    preferredPerformanceCaseId: 'simple-cycle-iso',
    performanceCases: [performance({
      id: 'simple-cycle-iso',
      label: 'Simple-cycle ISO base',
      frequencyHz: [50, 60],
      duty: 'iso-rating',
      fuel: 'Natural gas',
      electricalOutputKw: 41_000,
      ratingScope: 'generator-terminals',
      electricalEfficiencyPct: 36.2,
      heatRate: { valueKjPerKwh: 9_949, heatingValueBasis: 'LHV' },
      exhaustMassFlowKgS: 114,
      exhaustTemperatureC: 569,
      minimumLoadPct: 50,
      startTimeMinutes: 22,
      rampRateMwPerMinute: 3.4,
      referenceConditions: 'OEM ISO base reference conditions.',
      sourceIds: ['mhi-h25-current'],
      notes: 'Public combined-cycle values are not merged into this simple-cycle case.',
    })],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['mhi-h25-current']),
    physicalReferences: [physical({
      id: 'gas-turbine-core',
      label: 'Gas-turbine core',
      scope: 'engine-only',
      lengthM: 7.9,
      widthM: 3.8,
      heightM: 3.9,
      massTonnes: 55,
      configurationScope: 'exact-product',
      coverageScope: 'per-product',
      includes: ['prime-mover'],
      maintenanceClearanceIncluded: false,
      fireAccessIncluded: false,
      sourceIds: ['mhi-h25-current'],
      limitation: 'Gas-turbine core only; excludes generator, auxiliaries, inlet/exhaust, fuel skid, transformer, cooling/HRSG, electrical yard, and all access envelopes.',
    })],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Complete package and installed plant footprint', 'Ambient/altitude correction curves', 'Part-load heat-rate curve', 'Fuel-gas pressure envelope', 'Study-grade electrical models'],
  }),
  product({
    id: 'siemens-energy-sgt800-62mw',
    manufacturer: 'Siemens Energy',
    model: 'SGT-800',
    variant: '62.5 MW rating; classic and single-lift package variants',
    technology: 'industrial-gas-turbine',
    packageScope: 'packaged-module',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['siemens-sgt800-current', 'siemens-sgt800-poster-2025'],
    preferredPerformanceCaseId: '62mw-iso',
    performanceCases: [performance({
      id: '62mw-iso',
      label: '62 MW-class ISO gross case',
      frequencyHz: [50, 60],
      duty: 'iso-rating',
      fuel: 'Natural gas',
      electricalOutputKw: 62_500,
      ratingScope: 'generator-terminals',
      electricalEfficiencyPct: 41.1,
      heatRate: { valueKjPerKwh: 8_759, heatingValueBasis: 'not-stated' },
      exhaustMassFlowKgS: 135.5,
      exhaustTemperatureC: 596,
      referenceConditions: 'Standard design; ISO ambient; natural gas; no intake or exhaust loss.',
      sourceIds: ['siemens-sgt800-current', 'siemens-sgt800-poster-2025'],
      notes: 'Source table does not explicitly label the heat-rate heating-value basis; it remains not-stated pending proposal confirmation.',
    })],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['siemens-sgt800-poster-2025']),
    physicalReferences: [
      physical({
        id: 'classic-package',
        label: 'Classic package with generator',
        scope: 'packaged-module',
        lengthM: 20.8,
        widthM: 7.3,
        heightM: 6.6,
        massTonnes: 285,
        configurationScope: 'product-family',
        coverageScope: 'per-product',
        includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure'],
        maintenanceClearanceIncluded: false,
        fireAccessIncluded: false,
        sourceIds: ['siemens-sgt800-poster-2025'],
        limitation: 'Includes AC generator but excludes inlet-filter housing, exhaust stack, external auxiliaries, balance of plant, and access envelopes; rating/configuration selection changes the package.',
      }),
      physical({
        id: 'single-lift-package',
        label: 'Single-lift package with generator',
        scope: 'packaged-module',
        lengthM: 22,
        widthM: 4.7,
        heightM: 5.3,
        massTonnes: 305,
        configurationScope: 'product-family',
        coverageScope: 'per-product',
        includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure'],
        maintenanceClearanceIncluded: false,
        fireAccessIncluded: false,
        sourceIds: ['siemens-sgt800-poster-2025'],
        limitation: 'Includes AC generator but excludes inlet-filter housing, exhaust stack, external auxiliaries, balance of plant, and access envelopes; use only after selecting the package configuration.',
      }),
    ],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Heating-value basis confirmation', 'Numeric start/ramp/minimum load', 'Part-load and ambient curves', 'Installed plant GA and clearances', 'Study-grade electrical models'],
  }),
  product({
    id: 'rehlko-kg125',
    manufacturer: 'Rehlko',
    model: 'KG125',
    variant: 'Open natural-gas generator set',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['rehlko-kg125-g4289'],
    preferredPerformanceCaseId: '60hz-standby',
    performanceCases: [
      performance({
        id: '60hz-standby', label: '60 Hz standby', frequencyHz: [60], duty: 'standby', fuel: 'Natural gas; nominal 37 MJ/m³', electricalOutputKw: 125, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 700, referenceConditions: 'OEM standby rating; exact rating varies with voltage and phase.', sourceIds: ['rehlko-kg125-g4289'], notes: 'Rated exhaust flow 29 m³/min. Fuel use at 0/25/50/75/100% is 8.3/20.4/30.7/39.5/47.5 m³/h.' }),
      performance({
        id: '50hz-standby', label: '50 Hz standby', frequencyHz: [50], duty: 'standby', fuel: 'Natural gas; nominal 37 MJ/m³', electricalOutputKw: 100, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 650, referenceConditions: 'OEM standby rating; exact rating varies with voltage and phase.', sourceIds: ['rehlko-kg125-g4289'], notes: 'Rated exhaust flow 24 m³/min. Fuel use at 0/25/50/75/100% is 7.1/14.9/23.7/31.9/38.1 m³/h.' }),
    ],
    partLoadPoints: [0, 25, 50, 75, 100].flatMap((loadPct, index) => [
      { performanceCaseId: '60hz-standby', loadPct, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['rehlko-kg125-g4289'], notes: `Published natural-gas use: ${[8.3, 20.4, 30.7, 39.5, 47.5][index]} m³/h at ${loadPct}% load.` },
      { performanceCaseId: '50hz-standby', loadPct, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['rehlko-kg125-g4289'], notes: `Published natural-gas use: ${[7.1, 14.9, 23.7, 31.9, 38.1][index]} m³/h at ${loadPct}% load.` },
    ]),
    heatRecoveryCases: [],
    electrical: limitedElectrical(['rehlko-kg125-g4289'], 'Published rating is 156 kVA at 60 Hz and 125 kVA at 50 Hz, both at 0.8 power factor; detailed alternator study data are absent.', { ratedPowerFactor: 0.8 }),
    physicalReferences: [physical({ id: 'open-genset', label: 'Open generator set', scope: 'generator-set-skid', lengthM: 2.8, widthM: 1.12, heightM: 1.528, massTonnes: 1.207, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'cooling'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['rehlko-kg125-g4289'], limitation: 'OEM reference-only dimensions explicitly not intended for installation planning.' })],
    clearances: [],
    additionalTestFitReservations: STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Efficiency and heat rate', 'Numeric start time and minimum stable load', 'Installed enclosure/GA and clearances', 'Emissions and acoustic source data', 'Study-grade electrical models'],
  }),
  product({
    id: 'generac-sg175',
    manufacturer: 'Generac',
    model: 'SG175',
    variant: '60 Hz natural-gas industrial generator set',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['generac-sg175-2024'],
    preferredPerformanceCaseId: '60hz-prime',
    performanceCases: [
      performance({ id: '60hz-prime', label: '60 Hz prime', frequencyHz: [60], duty: 'prime', fuel: 'Natural gas; heating value not stated', electricalOutputKw: 158, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 714, referenceConditions: 'Three-phase 0.8 pf prime rating.', sourceIds: ['generac-sg175-2024'], notes: 'Rated post-silencer exhaust flow 33 m³/min; fuel use at 25/50/75/100% is 23.8/39.1/51.0/62.9 m³/h.' }),
      performance({ id: '60hz-standby', label: '60 Hz standby / demand response', frequencyHz: [60], duty: 'standby', fuel: 'Natural gas; heating value not stated', electricalOutputKw: 175, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 732, referenceConditions: 'Three-phase 0.8 pf standby/demand-response rating.', sourceIds: ['generac-sg175-2024'], notes: 'Rated post-silencer exhaust flow 36 m³/min; fuel use at 25/50/75/100% is 25.5/40.8/56.1/68.0 m³/h.' }),
    ],
    partLoadPoints: [25, 50, 75, 100].flatMap((loadPct, index) => [
      { performanceCaseId: '60hz-prime', loadPct, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['generac-sg175-2024'], notes: `Published prime natural-gas use: ${[23.8, 39.1, 51, 62.9][index]} m³/h.` },
      { performanceCaseId: '60hz-standby', loadPct, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['generac-sg175-2024'], notes: `Published standby natural-gas use: ${[25.5, 40.8, 56.1, 68][index]} m³/h.` },
    ]),
    heatRecoveryCases: [],
    electrical: limitedElectrical(['generac-sg175-2024'], 'Published kVA is 198 prime and 219 standby at 0.8 power factor; detailed study data are absent.', { ratedPowerFactor: 0.8 }),
    physicalReferences: [
      physical({ id: 'open-set', label: 'Open generator set', scope: 'generator-set-skid', lengthM: 3.251, widthM: 1.344, heightM: 1.582, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'cooling'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['generac-sg175-2024'], limitation: 'Approximate estimating dimensions; obtain the exact configuration GA.' }),
      physical({ id: 'weather-enclosure', label: 'Weather enclosure', scope: 'enclosure', lengthM: 3.922, widthM: 1.372, heightM: 1.773, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'cooling', 'acoustic-enclosure'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['generac-sg175-2024'], limitation: 'Approximate estimating dimensions; equipment options, service access, air recirculation, exhaust, fuel, and fire access require the exact GA.' }),
      physical({ id: 'level-1-sound', label: 'Level-1 sound enclosure', scope: 'enclosure', lengthM: 4.569, widthM: 1.372, heightM: 1.773, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'cooling', 'acoustic-enclosure'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['generac-sg175-2024'], limitation: 'Approximate estimating dimensions; obtain the selected enclosure GA.' }),
      physical({ id: 'level-2-sound', label: 'Level-2 sound enclosure', scope: 'enclosure', lengthM: 3.922, widthM: 1.372, heightM: 2.37, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'cooling', 'acoustic-enclosure'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['generac-sg175-2024'], limitation: 'Approximate estimating dimensions; obtain the selected enclosure GA.' }),
    ],
    clearances: [],
    additionalTestFitReservations: STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Fuel heating-value basis and efficiency', 'Numeric start/ramp/minimum load', 'Exact option-specific GA and clearances', 'Study-grade electrical models'],
  }),
  product({
    id: 'innio-jenbacher-j320-1066',
    manufacturer: 'INNIO Jenbacher',
    model: 'J320',
    variant: 'Natural gas, NOx <500 mg/Nm³, 1,066 kW class',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'combined-heat-and-power-package',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['innio-j320-2025'],
    preferredPerformanceCaseId: '50hz-full-load',
    performanceCases: [
      performance({ id: '50hz-full-load', label: '50 Hz full load', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas', electricalOutputKw: 1_066, ratingScope: 'generator-terminals', electricalEfficiencyPct: 42.7, referenceConditions: 'ISO 3046 full load; NOx <500 mg/Nm³.', sourceIds: ['innio-j320-2025'] }),
      performance({ id: '60hz-full-load', label: '60 Hz / 1,800 rpm full load', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas', electricalOutputKw: 1_062, ratingScope: 'generator-terminals', electricalEfficiencyPct: 39.2, referenceConditions: 'ISO 3046 full load; NOx <500 mg/Nm³.', sourceIds: ['innio-j320-2025'] }),
    ],
    partLoadPoints: [],
    heatRecoveryCases: [
      { performanceCaseId: '50hz-full-load', jacketWaterKw: null, firstStageChargeAirKw: null, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: null, totalThermalOutputKw: 1_157, sourceIds: ['innio-j320-2025'], notes: 'Published 46.3% thermal and 89.0% total efficiency; heat basis is exhaust cooled to 120°C, ±8%.' },
      { performanceCaseId: '60hz-full-load', jacketWaterKw: null, firstStageChargeAirKw: null, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: null, totalThermalOutputKw: 1_361, sourceIds: ['innio-j320-2025'], notes: 'Published 50.2% thermal and 89.4% total efficiency; heat basis is exhaust cooled to 120°C, ±8%.' },
    ],
    electrical: limitedElectrical(['innio-j320-2025']),
    physicalReferences: [
      physical({ id: 'generator-set', label: 'Generator set', scope: 'generator-set-skid', lengthM: 6, widthM: 1.8, heightM: 2.3, massTonnes: 13.9, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['innio-j320-2025'], limitation: 'Empty genset dimensions; excludes cooling, heat recovery, service access, exhaust, fuel, electrical, and fire reservations.' }),
      physical({ id: 'chp-system', label: 'Cogeneration system', scope: 'packaged-module', lengthM: 6, widthM: 2.2, heightM: 2.3, massTonnes: 14.4, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'heat-recovery'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['innio-j320-2025'], limitation: 'Empty CHP module only; external cooling, service, air/exhaust, gas train, electrical, fire, road, and crane requirements remain project-specific.' }),
      physical({ id: '40ft-container', label: '40-foot container, horizontal silencer base', scope: 'enclosure', lengthM: 12.2, widthM: 2.5, heightM: 5.3, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure', 'exhaust'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['innio-j320-2025'], limitation: 'Height is the upper end of the published 2.6–5.3 m range. Container scope does not establish external cooling, service, gas, electrical, fire, road, or crane access.' }),
    ],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Heating-value basis', 'Raw exhaust temperature and flow', 'Part-load curve', 'Numeric start/ramp/minimum load', 'Project GA and clearances', 'Study-grade electrical models'],
  }),
  product({
    id: 'cummins-c1100n6c',
    manufacturer: 'Cummins',
    model: 'C1100N6C',
    variant: '60 Hz continuous gas generator set',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'status-unconfirmed',
    fuels: ['natural gas'],
    identitySourceIds: ['cummins-c1100n6c-d6204'],
    preferredPerformanceCaseId: '60hz-continuous',
    performanceCases: [performance({
      id: '60hz-continuous', label: '60 Hz continuous full load', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas, LHV 33.44 MJ/Nm³', electricalOutputKw: 1_100, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 42.4, fuelInputKw: 2_597, exhaustMassFlowKgS: 1.7, exhaustTemperatureC: 407, referenceConditions: 'ISO 3046; 1,013 mbar; 25°C inlet; generator terminals at pf 1; assumed 97% alternator efficiency; includes engine coolant pumps and 10 kW customer-pump allowance.', sourceIds: ['cummins-c1100n6c-d6204']
    })],
    partLoadPoints: [
      { performanceCaseId: '60hz-continuous', loadPct: 90, electricalOutputKw: null, electricalEfficiencyPct: 42.1, heatRate: null, fuelInputKw: 2_349, sourceIds: ['cummins-c1100n6c-d6204'], notes: 'Exhaust 1.53 kg/s at 415°C; exhaust heat available to 105°C is 538 kW.' },
      { performanceCaseId: '60hz-continuous', loadPct: 75, electricalOutputKw: null, electricalEfficiencyPct: 41.9, heatRate: null, fuelInputKw: 1_971, sourceIds: ['cummins-c1100n6c-d6204'], notes: 'Exhaust 1.29 kg/s at 433°C; exhaust heat available to 105°C is 480 kW.' },
      { performanceCaseId: '60hz-continuous', loadPct: 50, electricalOutputKw: null, electricalEfficiencyPct: 39.1, heatRate: null, fuelInputKw: 1_407, sourceIds: ['cummins-c1100n6c-d6204'], notes: 'Exhaust 0.89 kg/s at 459°C; exhaust heat available to 105°C is 359 kW.' },
    ],
    heatRecoveryCases: [{ performanceCaseId: '60hz-continuous', jacketWaterKw: null, firstStageChargeAirKw: null, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: 581, totalThermalOutputKw: null, sourceIds: ['cummins-c1100n6c-d6204'], notes: 'Available full-load exhaust heat cooled to 105°C.' }],
    electrical: limitedElectrical(['cummins-c1100n6c-d6204'], 'Published performance assumes 97% alternator efficiency at pf 1; study-grade alternator and controls data are not public.', { ratedPowerFactor: 1 }),
    physicalReferences: [physical({ id: 'standard-genset', label: 'Standard generator set', scope: 'generator-set-skid', lengthM: 5.12, widthM: 2.23, heightM: 2.77, massTonnes: 15.625, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['cummins-c1100n6c-d6204'], limitation: 'Wet standard-genset dimensions with remote radiator; no enclosure, radiator bank, heat-recovery equipment, service clearance, electrical, fire, road, or crane envelope.' })],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Current production status', 'Numeric start/ramp/minimum load', 'Installed enclosure/GA and clearances', 'Study-grade electrical models'],
  }),
  product({
    id: 'caterpillar-g3520h-50hz',
    manufacturer: 'Caterpillar',
    model: 'G3520H',
    variant: '50 Hz high-efficiency, no-pumps, 250 mg/Nm³ NOx configuration',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas', 'hydrogen blend up to published limit'],
    identitySourceIds: ['cat-g3520h-lehe1267', 'cat-g3520h-current'],
    preferredPerformanceCaseId: '50hz-continuous',
    performanceCases: [performance({ id: '50hz-continuous', label: '50 Hz continuous', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas', electricalOutputKw: 2_500, ratingScope: 'generator-terminals', powerFactor: 0.8, heatRate: { valueKjPerKwh: 8_290, heatingValueBasis: 'not-stated' }, exhaustTemperatureC: 391, referenceConditions: 'Continuous non-varying load; unlimited time; average output 70–100%; high-efficiency/no-pumps/250 mg NOx configuration.', sourceIds: ['cat-g3520h-lehe1267'], notes: 'Source row does not explicitly label LHV/HHV; the basis remains not-stated. Full-load specific exhaust flow is 4.22 Nm³/bkWh and 5.33 kg/bkWh.' })],
    partLoadPoints: [
      { performanceCaseId: '50hz-continuous', loadPct: 75, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: { valueKjPerKwh: 8_390, heatingValueBasis: 'not-stated' }, fuelInputKw: null, sourceIds: ['cat-g3520h-lehe1267'], notes: 'Fuel heat rate with fan; source basis remains not-stated.' },
      { performanceCaseId: '50hz-continuous', loadPct: 50, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: { valueKjPerKwh: 8_700, heatingValueBasis: 'not-stated' }, fuelInputKw: null, sourceIds: ['cat-g3520h-lehe1267'], notes: 'Fuel heat rate with fan; source basis remains not-stated.' },
    ],
    heatRecoveryCases: [{ performanceCaseId: '50hz-continuous', jacketWaterKw: 650, firstStageChargeAirKw: null, secondStageChargeAirKw: 325, lubeOilKw: null, exhaustKw: 1_117, totalThermalOutputKw: null, sourceIds: ['cat-g3520h-lehe1267'], notes: 'Full-load heat rejection; exhaust heat is to 120°C. Engine/generator atmosphere rejection is 204 kW.' }],
    electrical: limitedElectrical(['cat-g3520h-lehe1267'], 'Published set rating is 3,125 kVA at 0.8 pf and ISO 8528-5 G1 capable; the public sheet does not provide a complete fault/controls model.', { ratedKva: 3_125, ratedPowerFactor: 0.8 }),
    physicalReferences: [
      physical({ id: 'lehe1267-50hz-set', label: 'LEHE1267 50 Hz open set', scope: 'generator-set-skid', lengthM: 6.94, widthM: 2.173, heightM: 2.473, massTonnes: 24.8, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['cat-g3520h-lehe1267'], limitation: 'OEM explicitly says these reference dimensions are not for installation design. They apply to the selected 50 Hz configuration.' }),
      physical({ id: 'current-portal-generic', label: 'Current portal generic configuration', scope: 'generator-set-skid', lengthM: 7.668, widthM: 2.173, heightM: 2.473, massTonnes: 25.285, configurationScope: 'product-family', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['cat-g3520h-current'], limitation: 'Generic portal configuration. Its 25,285 kg metric value conflicts with the displayed 54,675 lb (24,800 kg). Preserve raw values; do not normalize or use for engineering installation.' }),
    ],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Confirmed heat-rate heating-value basis', 'Option-specific outline drawing and installed GA', 'Numeric start/ramp/minimum stable load', 'Study-grade alternator, AVR, and governor models'],
    notes: 'Dimension and mass variants are deliberately quarantined by source/configuration. The portal mass pair is internally inconsistent.',
  }),
  product({
    id: 'caterpillar-3512b-50hz',
    manufacturer: 'Caterpillar',
    model: '3512B',
    variant: '50 Hz diesel, low-fuel-consumption 90°C SCAC',
    technology: 'compression-ignition-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['No. 2 diesel'],
    identitySourceIds: ['cat-3512b-lehe1757'],
    preferredPerformanceCaseId: '50hz-continuous',
    performanceCases: [
      performance({ id: '50hz-continuous', label: '50 Hz continuous', frequencyHz: [50], duty: 'continuous', fuel: 'No. 2 diesel; LHV 42,780 kJ/kg; density 850 g/L at 15°C', electricalOutputKw: 1_056, ratingScope: 'generator-terminals', powerFactor: 0.8, referenceConditions: 'ISO 3046-1; 50 Hz continuous rating.', sourceIds: ['cat-3512b-lehe1757'] }),
      performance({ id: '50hz-prime', label: '50 Hz prime', frequencyHz: [50], duty: 'prime', fuel: 'No. 2 diesel; LHV 42,780 kJ/kg; density 850 g/L at 15°C', electricalOutputKw: 1_200, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 492.7, referenceConditions: 'ISO 3046-1; 50 Hz prime rating.', sourceIds: ['cat-3512b-lehe1757'], notes: 'Prime exhaust flow 259.7 m³/min.' }),
      performance({ id: '50hz-standby', label: '50 Hz standby', frequencyHz: [50], duty: 'standby', fuel: 'No. 2 diesel; LHV 42,780 kJ/kg; density 850 g/L at 15°C', electricalOutputKw: 1_300, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 492.9, referenceConditions: 'ISO 3046-1; low-fuel-consumption 90°C SCAC standby row.', sourceIds: ['cat-3512b-lehe1757'], notes: 'Standby exhaust flow 271.2 m³/min; accepts 100% block load in one step; ISO 8528-5 G3.' }),
    ],
    partLoadPoints: [100, 75, 50, 25].map((loadPct, index) => ({ performanceCaseId: '50hz-standby', loadPct, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['cat-3512b-lehe1757'], notes: `Published standby diesel use: ${[327.5, 249.2, 174.5, 102.6][index]} L/h.` })),
    heatRecoveryCases: [{ performanceCaseId: '50hz-standby', jacketWaterKw: 568, firstStageChargeAirKw: 241, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: 1_237, totalThermalOutputKw: null, sourceIds: ['cat-3512b-lehe1757'], notes: 'Standby heat rejection; atmosphere 138 kW and alternator 58 kW are additional rejected heat.' }],
    electrical: limitedElectrical(['cat-3512b-lehe1757'], 'Published ratings are at 0.8 pf and ISO 8528-5 G3; detailed alternator/controls study models are absent.', { ratedPowerFactor: 0.8 }),
    physicalReferences: [physical({ id: 'open-genset', label: 'Open reference generator set', scope: 'generator-set-skid', lengthM: 5.404, widthM: 2.286, heightM: 2.411, massTonnes: 11.621, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['cat-3512b-lehe1757'], limitation: 'OEM reference dimensions explicitly not for installation design; excludes cooling/radiator, exhaust, fuel system, enclosure, electrical equipment, service clearances, and fire access.' })],
    clearances: [],
    additionalTestFitReservations: STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Efficiency/heat-rate curve', 'Numeric starting time and minimum load', 'Option-specific GA and installed reservations', 'Study-grade electrical models'],
  }),
  product({
    id: 'mwm-tcg3020-v20',
    manufacturer: 'MWM',
    model: 'TCG 3020 V20',
    variant: 'Natural-gas high-efficiency P variant, NOx ≤500 mg/Nm³',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['mwm-tcg3020-2024'],
    preferredPerformanceCaseId: '50hz-full-load',
    performanceCases: [
      performance({ id: '50hz-full-load', label: '50 Hz full load', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas, methane number 80', electricalOutputKw: 2_300, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 45, referenceConditions: 'ISO 8528-1; 0.4 kV; pf 1; full-load indicative value.', sourceIds: ['mwm-tcg3020-2024'] }),
      performance({ id: '60hz-full-load', label: '60 Hz full load', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas, methane number 80', electricalOutputKw: 2_300, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 44.4, referenceConditions: 'ISO 8528-1; pf 1; full-load indicative value.', sourceIds: ['mwm-tcg3020-2024'] }),
    ],
    partLoadPoints: [],
    heatRecoveryCases: [
      { performanceCaseId: '50hz-full-load', jacketWaterKw: null, firstStageChargeAirKw: null, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: null, totalThermalOutputKw: 2_164, sourceIds: ['mwm-tcg3020-2024'], notes: 'Published 42.3% thermal and 87.3% total efficiency, with exhaust cooled to 120°C.' },
      { performanceCaseId: '60hz-full-load', jacketWaterKw: null, firstStageChargeAirKw: null, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: null, totalThermalOutputKw: 2_201, sourceIds: ['mwm-tcg3020-2024'], notes: 'Published 42.5% thermal and 86.9% total efficiency, with exhaust cooled to 120°C.' },
    ],
    electrical: limitedElectrical(['mwm-tcg3020-2024'], 'Public case is 0.4 kV at pf 1; detailed machine/control models are not provided.', { nominalVoltagesKv: [0.4], ratedPowerFactor: 1 }),
    physicalReferences: [
      physical({ id: '50hz-transport', label: '50 Hz transport envelope', scope: 'transport-envelope', lengthM: 6.6, widthM: 1.815, heightM: 2.19, massTonnes: 21.4, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['mwm-tcg3020-2024'], limitation: 'Transport envelope only; separately installed balance-of-plant and all operating/service/fire reservations are excluded.' }),
      physical({ id: '60hz-transport', label: '60 Hz transport envelope', scope: 'transport-envelope', lengthM: 7.738, widthM: 1.815, heightM: 2.551, massTonnes: 21.2, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['mwm-tcg3020-2024'], limitation: 'Transport envelope only; separately installed balance-of-plant and all operating/service/fire reservations are excluded.' }),
    ],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Heating-value basis', 'Raw exhaust temperature and mass flow', 'Part-load curve', 'Numeric start/ramp/minimum load', 'Installed GA and clearances', 'Study-grade electrical models'],
  }),
  product({
    id: 'mtu-20v4000-ds3000-2790',
    manufacturer: 'Rolls-Royce mtu',
    model: '20V4000 DS3000',
    variant: '2,790 kW, 60 Hz, Data Center Continuous Power',
    technology: 'compression-ignition-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['No. 2 diesel', 'HVO'],
    identitySourceIds: ['mtu-ds3000-2026'],
    preferredPerformanceCaseId: '60hz-dccp',
    performanceCases: [performance({ id: '60hz-dccp', label: '60 Hz Data Center Continuous Power', frequencyHz: [60], duty: 'data-center-continuous', fuel: 'No. 2 diesel or HVO', electricalOutputKw: 2_790, ratingScope: 'generator-terminals', powerFactor: 0.8, exhaustTemperatureC: 480, referenceConditions: 'Nominal 25°C and 300 m altitude; no runtime limit; 100% load factor; 10% overload.', sourceIds: ['mtu-ds3000-2026'], notes: 'Full-load fuel use 712 L/h; exhaust flow 624 m³/min; accepts rated load in one step and is ISO 8528-5 transient tested.' })],
    partLoadPoints: [
      { performanceCaseId: '60hz-dccp', loadPct: 75, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['mtu-ds3000-2026'], notes: 'Published diesel/HVO use 553 L/h.' },
      { performanceCaseId: '60hz-dccp', loadPct: 50, electricalOutputKw: null, electricalEfficiencyPct: null, heatRate: null, fuelInputKw: null, sourceIds: ['mtu-ds3000-2026'], notes: 'Published diesel/HVO use 390 L/h.' },
    ],
    heatRecoveryCases: [{ performanceCaseId: '60hz-dccp', jacketWaterKw: 1_040, firstStageChargeAirKw: 770, secondStageChargeAirKw: null, lubeOilKw: null, exhaustKw: null, totalThermalOutputKw: null, sourceIds: ['mtu-ds3000-2026'], notes: 'Published coolant and aftercooler rejection; ambient rejection is 253 kW.' }],
    electrical: limitedElectrical(['mtu-ds3000-2026'], 'Published rating is 3,487 kVA at 0.8 pf; detailed alternator/controls models are absent.', { ratedKva: 3_487, ratedPowerFactor: 0.8 }),
    physicalReferences: [physical({ id: 'open-unit', label: 'Open unit', scope: 'generator-set-skid', lengthM: 7.335, widthM: 3.345, heightM: 3.807, massTonnes: 25.498, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['mtu-ds3000-2026'], limitation: 'Estimated open-unit dimensions; voltage affects length and the OEM explicitly says not to use the figures for installation design.' })],
    clearances: [],
    additionalTestFitReservations: STANDARD_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Efficiency/heat rate and fuel heating-value basis', 'Numeric start time/minimum load', 'Exact voltage/configuration GA and clearances', 'Fuel-storage reservation', 'Study-grade electrical models'],
  }),
  product({
    id: 'wartsila-20v34sg',
    manufacturer: 'Wärtsilä',
    model: '20V34SG',
    variant: 'Natural-gas generator set',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['wartsila-34sg-2023', 'wartsila-34sg-current'],
    preferredPerformanceCaseId: '50hz-full-load',
    performanceCases: [
      performance({ id: '50hz-full-load', label: '50 Hz full load', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas, LHV >28 MJ/Nm³, methane number >80', electricalOutputKw: 9_795, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 48.9, heatRate: { valueKjPerKwh: 7_363, heatingValueBasis: 'LHV' }, minimumLoadPct: 10, referenceConditions: 'Generator terminals; 100 kPa; 25°C suction air; 30% RH; without engine-driven pumps; site pf 1; efficiency and heat rate ±5%.', sourceIds: ['wartsila-34sg-2023', 'wartsila-34sg-current'], notes: 'Fast full load is <2 min; regular full load <5 min; grid connection 30 s; hot ramp is >100% rated load/min. Bounds remain notes rather than exact scalar simulation inputs.' }),
      performance({ id: '60hz-full-load', label: '60 Hz full load', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas, LHV >28 MJ/Nm³, methane number >80', electricalOutputKw: 9_388, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 48.8, heatRate: { valueKjPerKwh: 7_374, heatingValueBasis: 'LHV' }, minimumLoadPct: 10, referenceConditions: 'Generator terminals; 100 kPa; 25°C suction air; 30% RH; without engine-driven pumps; site pf 1; efficiency and heat rate ±5%.', sourceIds: ['wartsila-34sg-2023', 'wartsila-34sg-current'], notes: 'Fast full load is <2 min; regular full load <5 min; grid connection 30 s; hot ramp is >100% rated load/min. Bounds remain notes rather than exact scalar simulation inputs.' }),
    ],
    partLoadPoints: [],
    heatRecoveryCases: [],
    electrical: limitedElectrical(['wartsila-34sg-2023'], 'Site rating is published at pf 1; study-grade electrical models are not in the public leaflet.', { ratedPowerFactor: 1 }),
    physicalReferences: [physical({ id: 'maximum-transport', label: 'Maximum transport envelope', scope: 'transport-envelope', lengthM: 13.142, widthM: 3.35, heightM: 4.573, massTonnes: 136, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['wartsila-34sg-2023'], limitation: 'Maximum transport envelope only; dismantling can reduce transport height/weight. It is not a plant, operating, maintenance, or fire-access envelope.' })],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Exhaust mass flow and temperature', 'Part-load heat-rate curve', 'Installed module/plant GA and clearances', 'Contractual start/ramp curve', 'Study-grade electrical models'],
    notes: 'The 2023 leaflet and current webpage differ on plant-level minimum load; source versions are not merged.',
  }),
  product({
    id: 'bergen-b3645v20',
    manufacturer: 'Bergen Engines',
    model: 'B36:45V20',
    variant: 'Natural-gas complete generator set, January 2026 fact sheet',
    technology: 'spark-ignited-gas-engine',
    packageScope: 'generator-set',
    productStatus: 'current',
    fuels: ['natural gas'],
    identitySourceIds: ['bergen-b3645v-2026', 'bergen-b3645v-current'],
    preferredPerformanceCaseId: '50hz-mcr',
    performanceCases: [
      performance({ id: '50hz-mcr', label: '50 Hz maximum continuous rating', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas, LHV 36 MJ/Nm³, methane number 80', electricalOutputKw: 11_785, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 49, exhaustMassFlowKgS: 18.4167, exhaustTemperatureC: 370, referenceConditions: '100% load; two engine-driven pumps included; ISO 3046/8528 and IEC 60034; 25°C turbocharger suction; exhaust/heat data ±5%.', sourceIds: ['bergen-b3645v-2026'] }),
      performance({ id: '60hz-mcr', label: '60 Hz maximum continuous rating', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas, LHV 36 MJ/Nm³, methane number 80', electricalOutputKw: 11_280, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 48.9, exhaustMassFlowKgS: 17.6389, exhaustTemperatureC: 375, referenceConditions: '100% load; two engine-driven pumps included; ISO 3046/8528 and IEC 60034; 25°C turbocharger suction; exhaust/heat data ±5%.', sourceIds: ['bergen-b3645v-2026'] }),
    ],
    partLoadPoints: [],
    heatRecoveryCases: [{ performanceCaseId: '50hz-mcr', jacketWaterKw: 1_685, firstStageChargeAirKw: 2_575, secondStageChargeAirKw: 460, lubeOilKw: 1_065, exhaustKw: null, totalThermalOutputKw: null, sourceIds: ['bergen-b3645v-2026'], notes: 'Published recoverable/rejected heat streams at 50 Hz MCR.' }],
    electrical: limitedElectrical(['bergen-b3645v-2026'], 'Published plant data use pf 1; detailed alternator/control models are not public.', { ratedPowerFactor: 1 }),
    physicalReferences: [physical({ id: 'complete-generator-set-2026', label: 'Complete generator set', scope: 'generator-set-skid', lengthM: 14.2, widthM: 3.75, heightM: 4.75, massTonnes: 155, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls'], maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['bergen-b3645v-2026'], limitation: 'Complete land genset only; auxiliaries, cooling, exhaust, fuel conditioning, electrical equipment, maintenance/crane access, and fire reservations are excluded.' })],
    clearances: [],
    additionalTestFitReservations: CHP_TEST_FIT_RESERVATIONS,
    simulationCatalogId: null,
    dataGaps: ['Published heat rate', 'Part-load curve', 'Numeric start/ramp/minimum load', 'Installed plant GA and clearances', 'Study-grade electrical models'],
    notes: 'The January 2026 fact sheet is retained as a versioned configuration; live-page dimensional differences are not merged.',
  }),
  ...([
    { id: 'capstone-c65', model: 'C65', outputKw: 65, efficiencyPct: 28, heatRate: 12_400, exhaustC: 309, exhaustKgS: 0.49, lengthM: 1.95, widthM: 0.76, heightM: 1.91, massTonnes: 0.758, sourceId: 'capstone-c65-current', fuels: ['natural gas', 'ULSD No. 2', 'biogas', 'associated gas', 'sour gas', 'propane'] },
    { id: 'capstone-c200s', model: 'C200S', outputKw: 200, efficiencyPct: 33, heatRate: 10_900, exhaustC: 280, exhaustKgS: 1.3, lengthM: 2.5, widthM: 3.0, heightM: 3.03, massTonnes: 5.2, sourceId: 'capstone-c200s-current', fuels: ['natural gas', 'biogas', 'raw associated gas', 'sour gas'] },
    { id: 'capstone-c600s', model: 'C600S', outputKw: 600, efficiencyPct: 33, heatRate: 10_900, exhaustC: 280, exhaustKgS: 4.0, lengthM: 5.8, widthM: 3.0, heightM: 3.03, massTonnes: 13.3, sourceId: 'capstone-c600s-current', fuels: ['natural gas', 'biogas', 'raw associated gas', 'sour gas'] },
    { id: 'capstone-c800s', model: 'C800S', outputKw: 800, efficiencyPct: 33, heatRate: 10_900, exhaustC: 280, exhaustKgS: 5.3, lengthM: 7.5, widthM: 3.0, heightM: 3.03, massTonnes: 15.2, sourceId: 'capstone-c800s-current', fuels: ['natural gas', 'biogas', 'raw associated gas', 'sour gas'] },
    { id: 'capstone-c1000s', model: 'C1000S', outputKw: 1_000, efficiencyPct: 33, heatRate: 10_900, exhaustC: 280, exhaustKgS: 6.7, lengthM: 9.1, widthM: 3.0, heightM: 3.03, massTonnes: 17.1, sourceId: 'capstone-c1000s-current', fuels: ['natural gas', 'biogas', 'raw associated gas', 'sour gas'] },
  ] as const).map(seed => evidenceProduct({
    id: seed.id,
    manufacturer: 'Capstone Energy',
    model: seed.model,
    variant: 'Grid-connect enclosure package at ISO reference conditions',
    technology: 'microturbine',
    packageScope: 'packaged-module',
    fuels: seed.fuels,
    identitySourceIds: [seed.sourceId],
    preferredPerformanceCaseId: 'iso-grid-connect',
    performanceCases: [performance({
      id: 'iso-grid-connect', label: 'ISO grid-connect rating', frequencyHz: [50, 60], duty: 'iso-rating',
      fuel: 'Published gaseous-fuel reference; LHV basis', electricalOutputKw: seed.outputKw,
      ratingScope: 'package-net', electricalEfficiencyPct: seed.efficiencyPct,
      heatRate: { valueKjPerKwh: seed.heatRate, heatingValueBasis: 'LHV' },
      exhaustMassFlowKgS: seed.exhaustKgS, exhaustTemperatureC: seed.exhaustC,
      referenceConditions: 'ISO 15°C; 14.696 psia; 60% RH; LHV basis.', sourceIds: [seed.sourceId],
    })],
    electrical: limitedElectrical(
      [seed.sourceId],
      '400–480 VAC, 50/60 Hz power-electronic output is public. This is not a synchronous alternator; project inverter/fault/RMS/EMT models remain required.',
      { nominalVoltagesKv: [0.4, 0.48] },
    ),
    physicalReferences: [physical({
      id: 'grid-connect-enclosure', label: 'Grid-connect enclosure package', scope: 'enclosure',
      lengthM: seed.lengthM, widthM: seed.widthM, heightM: seed.heightM, massTonnes: seed.massTonnes,
      configurationScope: 'exact-product', coverageScope: 'per-product',
      includes: ['prime-mover', 'controls', 'acoustic-enclosure'], sourceIds: [seed.sourceId],
      maintenanceClearanceIncluded: false, fireAccessIncluded: false,
      limitation: 'Published enclosure package only; excludes service access, fire separation, fuel treatment, heat recovery, switchgear/transformer, roads, and civil works.',
    })],
    dataGaps: ['Part-load curve', 'Project ambient/altitude derate', 'Installed GA and service/fire access', 'Power-electronic fault, RMS, and EMT models', 'Project price and lead time'],
  })),
  evidenceProduct({
    id: 'ge-9ha01-simple-cycle-2025', manufacturer: 'GE Vernova', model: '9HA.01',
    variant: 'Current 50 Hz simple-cycle net-plant case', technology: 'industrial-gas-turbine', packageScope: 'power-island',
    fuels: ['natural gas', 'hydrogen blend up to 50 vol% in published configuration'],
    identitySourceIds: ['ge-gas-power-catalog-2025'],
    preferredPerformanceCaseId: '50hz-simple-cycle', performanceCases: [performance({
      id: '50hz-simple-cycle', label: 'Current simple-cycle net rating', frequencyHz: [50], duty: 'iso-rating', fuel: 'Natural gas',
      electricalOutputKw: 448_000, ratingScope: 'plant-net', electricalEfficiencyPct: 42.9,
      heatRate: { valueKjPerKwh: 8_398, heatingValueBasis: 'LHV' }, referenceConditions: 'Net plant ISO natural-gas rating.',
      sourceIds: ['ge-gas-power-catalog-2025'],
    })],
    dataGaps: ['Pressure-ratio/TIT/component-efficiency reconciliation to current rating', 'Exhaust mass flow and temperature', 'Installed plant GA and clearances', 'Validated off-design model', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'ge-lm6000-pfplus-sprint', manufacturer: 'GE Vernova', model: 'LM6000 PF+ SPRINT',
    variant: 'Current aeroderivative package rating', technology: 'aeroderivative-gas-turbine', packageScope: 'packaged-module',
    fuels: ['natural gas'], identitySourceIds: ['ge-lm6000-current', 'ge-gas-power-catalog-2025'],
    preferredPerformanceCaseId: 'current-page', performanceCases: [performance({
      id: 'current-page', label: 'Current published package case', frequencyHz: [], duty: 'iso-rating', fuel: 'Natural gas',
      electricalOutputKw: 56_900, ratingScope: 'package-net', electricalEfficiencyPct: 41.0,
      heatRate: { valueKjPerKwh: 8_800, heatingValueBasis: 'LHV' }, startTimeMinutes: 5,
      referenceConditions: 'Current OEM ISO aeroderivative package case; frequency-specific row not established in the page summary.',
      sourceIds: ['ge-lm6000-current'],
    })],
    physicalReferences: [physical({
      id: 'velox-package', label: 'VELOX package envelope', scope: 'packaged-module', lengthM: 20.43, widthM: 4.1, heightM: 14.38,
      configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator', 'controls', 'acoustic-enclosure', 'air-inlet', 'exhaust'],
      maintenanceClearanceIncluded: false, fireAccessIncluded: false, sourceIds: ['ge-lm6000-current'],
      limitation: 'Factory package envelope only; balance of plant, external service/removal paths, fire access, transformer, roads, and civil reservations are excluded.',
    })],
    dataGaps: ['Frequency-specific current case', 'Pressure-ratio/TIT/component-efficiency reconciliation', 'Off-design validation', 'Installed GA and clearances', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'ge-7ha01-1x1-combined-cycle', manufacturer: 'GE Vernova', model: '7HA.01 1×1 combined cycle',
    variant: 'One gas turbine, HRSG, and steam cycle', technology: 'industrial-gas-turbine', packageScope: 'power-island',
    fuels: ['natural gas'], identitySourceIds: ['ge-7ha-power-plants-2025', 'ge-gas-power-catalog-2025'],
    preferredPerformanceCaseId: '60hz-1x1-cc', performanceCases: [performance({
      id: '60hz-1x1-cc', label: '1×1 combined-cycle net plant', frequencyHz: [60], duty: 'iso-rating', fuel: 'Natural gas',
      electricalOutputKw: 438_000, ratingScope: 'plant-net', electricalEfficiencyPct: 62.3,
      heatRate: { valueKjPerKwh: 5_783, heatingValueBasis: 'LHV' }, minimumLoadPct: 33, rampRateMwPerMinute: 55,
      referenceConditions: 'OEM net plant ISO natural-gas rating.', sourceIds: ['ge-7ha-power-plants-2025', 'ge-gas-power-catalog-2025'],
      notes: 'Published hot-start time is an inequality (<30 min) and is retained outside the scalar field.',
    })],
    simulationCatalogId: 'ge-7ha01-1x1-cc-60hz',
    dataGaps: ['Site ambient correction curve', 'Installed 1×1 GA and maintenance/fire clearances', 'Fuel-gas envelope', 'Cooling heat balance', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'siemens-scc6-5000f-1x1', manufacturer: 'Siemens Energy', model: 'SCC6-5000F 1×1',
    variant: '260 MW-class gas turbine combined-cycle configuration', technology: 'industrial-gas-turbine', packageScope: 'power-island',
    fuels: ['natural gas'], identitySourceIds: ['siemens-scc6-5000f-2025'], preferredPerformanceCaseId: '60hz-current-pdf',
    performanceCases: [performance({
      id: '60hz-current-pdf', label: 'Current PDF combined-cycle case', frequencyHz: [60], duty: 'iso-rating', fuel: 'Natural gas',
      electricalOutputKw: 391_000, ratingScope: 'plant-gross', electricalEfficiencyPct: 60.5,
      heatRate: { valueKjPerKwh: 5_950, heatingValueBasis: 'not-stated' },
      referenceConditions: 'Current OEM combined-cycle technical-data reference conditions; source labels the table gross.',
      sourceIds: ['siemens-scc6-5000f-2025'], notes: 'Retained as comparison-only because other current Siemens values and the existing planning normalization are not reconciled.',
    })],
    simulationCatalogId: 'siemens-scc6-5000f-1x1-60hz',
    dataGaps: ['Reconciled gross/net boundary', 'Heating-value basis', 'Ambient correction curve', 'Installed plant GA and clearances', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'ansaldo-gt36-2025', manufacturer: 'Ansaldo Energia', model: 'GT36', variant: 'Current 50 Hz simple-cycle case',
    technology: 'industrial-gas-turbine', packageScope: 'power-island', fuels: ['natural gas', 'hydrogen blend up to published configuration limit'],
    identitySourceIds: ['ansaldo-gt36-2025'], preferredPerformanceCaseId: '50hz-simple-cycle', performanceCases: [performance({
      id: '50hz-simple-cycle', label: 'Simple-cycle rating including OTC contribution', frequencyHz: [50], duty: 'iso-rating', fuel: '100% methane',
      electricalOutputKw: 563_000, ratingScope: 'plant-net', electricalEfficiencyPct: 43.0,
      exhaustMassFlowKgS: 1_065, exhaustTemperatureC: 630,
      referenceConditions: 'ISO; 100% methane; LHV/LVV basis; direct cooling; includes OTC contribution.', sourceIds: ['ansaldo-gt36-2025'],
    })],
    dataGaps: ['Published simple-cycle heat rate', 'Bounded package dimensions (only turbine length/mass are public)', 'Part-load curve and GT-only minimum', 'Installed GA and clearances', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'everllence-20v3544g', manufacturer: 'Everllence', model: '20V35/44G', variant: 'Standard single-stage gas engine',
    technology: 'spark-ignited-gas-engine', packageScope: 'engine-only', fuels: ['natural gas'], identitySourceIds: ['everllence-3544g-2025'],
    preferredPerformanceCaseId: '720rpm-shaft', performanceCases: [performance({
      id: '720rpm-shaft', label: '720 rpm engine-shaft rating', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas',
      electricalOutputKw: 10_200, ratingScope: 'engine-shaft', referenceConditions: 'OEM engine mechanical-rating reference conditions; no alternator loss or terminal rating applied.',
      sourceIds: ['everllence-3544g-2025'], notes: 'Field name is electricalOutputKw for catalog compatibility; ratingScope=engine-shaft is authoritative.',
    })],
    physicalReferences: [physical({ id: 'engine-standard', label: 'Standard engine', scope: 'engine-only', lengthM: 9.564, widthM: 4.448, heightM: 4.592, massTonnes: 113.5,
      configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover'], sourceIds: ['everllence-3544g-2025'],
      limitation: 'Engine-only envelope; alternator, coupling, cooling, exhaust, fuel equipment, auxiliaries, electrical equipment, and access are excluded.' })],
    dataGaps: ['Generator-terminal rating and efficiency', 'Heat rate/fuel input', 'Exhaust and recoverable heat', 'Complete genset/plant GA', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'guascor-g56hm-1500', manufacturer: 'Guascor Energy', model: 'G-56HM', variant: '1,500 rpm natural-gas genset',
    technology: 'spark-ignited-gas-engine', packageScope: 'generator-set', fuels: ['natural gas'], identitySourceIds: ['guascor-h-series-2023'],
    preferredPerformanceCaseId: '1500rpm-continuous', performanceCases: [performance({
      id: '1500rpm-continuous', label: '1,500 rpm continuous case', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas, methane number 80',
      electricalOutputKw: 1_337, ratingScope: 'generator-terminals', electricalEfficiencyPct: 43.4, fuelInputKw: 3_078,
      exhaustMassFlowKgS: 1.80278, exhaustTemperatureC: 408,
      referenceConditions: 'Sea level; 25°C; methane number 80; ISO 3046-1; published performance tolerance +5%.', sourceIds: ['guascor-h-series-2023'],
    })],
    heatRecoveryCases: [{ performanceCaseId: '1500rpm-continuous', jacketWaterKw: 716, firstStageChargeAirKw: null, secondStageChargeAirKw: 106, lubeOilKw: null, exhaustKw: 598, totalThermalOutputKw: 1_420, sourceIds: ['guascor-h-series-2023'], notes: 'Published jacket, LT circuit, and exhaust-to-reference-temperature heat streams.' }],
    physicalReferences: [physical({ id: 'genset-reference', label: 'Generator-set reference dimensions', scope: 'generator-set-skid', lengthM: 5.545, widthM: 2.141, heightM: 2.319, massTonnes: 12.2,
      configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator'], sourceIds: ['guascor-h-series-2023'],
      limitation: 'Generator-set reference only; proposal GA governs and external cooling, exhaust, fuel, electrical, maintenance, and fire reservations are excluded.' })],
    dataGaps: ['Part-load efficiency curve', 'Start/ramp/minimum-load data', 'Installed plant GA and clearances', 'Exact alternator and controls models', 'Project price and lead time'],
  }),
  evidenceProduct({
    id: 'wartsila-18v50sg', manufacturer: 'Wärtsilä', model: '18V50SG', variant: 'Natural-gas generator set',
    technology: 'spark-ignited-gas-engine', packageScope: 'generator-set', fuels: ['natural gas'], identitySourceIds: ['wartsila-50sg-current'],
    preferredPerformanceCaseId: '60hz-full-load', performanceCases: [
      performance({ id: '50hz-full-load', label: '50 Hz full load', frequencyHz: [50], duty: 'continuous', fuel: 'Natural gas', electricalOutputKw: 18_434, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 50.2, heatRate: { valueKjPerKwh: 7_165, heatingValueBasis: 'LHV' }, referenceConditions: 'Generator terminals; OEM leaflet reference conditions; pf 1.', sourceIds: ['wartsila-50sg-current'] }),
      performance({ id: '60hz-full-load', label: '60 Hz full load', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas', electricalOutputKw: 18_875, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 50.0, heatRate: { valueKjPerKwh: 7_207, heatingValueBasis: 'LHV' }, referenceConditions: 'Generator terminals; OEM leaflet reference conditions; pf 1.', sourceIds: ['wartsila-50sg-current'], notes: 'Full load <5 min; fast full load <2 min; grid connection 30 s; hot ramp >100% rated load/min.' }),
    ],
    electrical: limitedElectrical(['wartsila-50sg-current'], 'Power factor 1 is published; exact alternator and study-grade controls/dynamic models require the selected project configuration.', { ratedPowerFactor: 1 }),
    physicalReferences: [physical({ id: 'maximum-transport', label: 'Maximum transport envelope', scope: 'transport-envelope', lengthM: 18.747, widthM: 5.543, heightM: 6.257, massTonnes: 377,
      configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['prime-mover', 'generator'], maintenanceClearanceIncluded: false, fireAccessIncluded: false,
      sourceIds: ['wartsila-50sg-current'], limitation: 'Maximum transport envelope only; not an operating, maintenance, fire-access, or whole-plant reservation.' })],
    dataGaps: ['Exhaust mass flow and temperature', 'Exact part-load/off-design curve used by the solver', 'Installed plant GA and clearances', 'Study-grade electrical models', 'Project price and lead time'],
  }),
  evidenceProduct({
    id: 'wartsila-24x18v50sg-island', manufacturer: 'Wärtsilä', model: '24 × 18V50SG electrical island',
    variant: 'Arithmetic 24-engine 60 Hz equipment aggregation', technology: 'spark-ignited-gas-engine', packageScope: 'power-island',
    fuels: ['natural gas'], identitySourceIds: ['wartsila-50sg-current'], preferredPerformanceCaseId: '60hz-24-engine',
    performanceCases: [performance({ id: '60hz-24-engine', label: '24-engine arithmetic aggregation', frequencyHz: [60], duty: 'continuous', fuel: 'Natural gas',
      electricalOutputKw: 453_000, ratingScope: 'generator-terminals', powerFactor: 1, electricalEfficiencyPct: 50.0,
      heatRate: { valueKjPerKwh: 7_207, heatingValueBasis: 'LHV' },
      referenceConditions: '24 × 18.875 MW OEM 60 Hz generating-set rating; arithmetic aggregation only, not a plant guarantee.', sourceIds: ['wartsila-50sg-current'],
    })],
    simulationCatalogId: 'wartsila-24x18v50sg-electrical-island-60hz',
    dataGaps: ['Installed 24-engine plant GA', 'Shared-system/common-mode reliability', 'Plant auxiliaries and collection losses', 'Cooling and acoustic design', 'Study-grade electrical models'],
  }),
  evidenceProduct({
    id: 'caterpillar-g3520-2mw-screening', manufacturer: 'Caterpillar', model: 'G3520 2.0 MW',
    variant: '60 Hz critical-power family option', technology: 'spark-ignited-gas-engine', packageScope: 'generator-set',
    fuels: ['natural gas'], identitySourceIds: ['cat-g3520-fast-response-current'], preferredPerformanceCaseId: '60hz-2mw-family',
    performanceCases: [performance({ id: '60hz-2mw-family', label: 'Published 2.0 MW family option', frequencyHz: [60], duty: 'standby', fuel: 'Natural gas',
      electricalOutputKw: 2_000, ratingScope: 'generator-terminals', referenceConditions: 'Current OEM critical-power family announcement; exact selected emissions/rating sheet required.',
      sourceIds: ['cat-g3520-fast-response-current'], notes: 'The family also includes a 2.5 MW option; this row is deliberately limited to the stated 2.0 MW member.' })],
    dataGaps: ['Exact configuration rating sheet', 'Electrical efficiency/heat rate', 'Exhaust/heat balance', 'Installed GA and clearances', 'Study-grade alternator and control models'],
  }),
] satisfies readonly OemGenerationProduct[];

const electricalMachines = [
  {
    schemaVersion: OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
    id: 'stamford-s5l1d-h4-w311',
    manufacturer: 'STAMFORD | AvK',
    family: 'STAMFORD S5',
    model: 'S5L1D-H4',
    variant: 'Winding 311; self-excited AS440 or PMG with MX341/MX321/MX322',
    productStatus: 'current',
    identitySourceIds: ['stamford-s5l1d-h4-2025'],
    preferredRatingCaseId: '50hz-400v-h-rise',
    ratingCases: [
      {
        id: '50hz-400v-h-rise', label: '50 Hz, 400 V, Class-H temperature-rise rating', ratedKva: 750,
        ratedVoltageKv: 0.4, frequencyHz: 50, phases: 3, ratedPowerFactor: null, poles: 4, ratedSpeedRpm: 1_500,
        efficiencyPct: null, windingConnection: '12-lead, 2/3 pitch', enclosureProtection: 'IP23', coolingMethod: null,
        insulationClass: 'H', temperatureRiseClass: 'H', sourceIds: ['stamford-s5l1d-h4-2025'],
      },
      {
        id: '60hz-480v-h-rise', label: '60 Hz, 480 V, Class-H temperature-rise rating', ratedKva: 937,
        ratedVoltageKv: 0.48, frequencyHz: 60, phases: 3, ratedPowerFactor: null, poles: 4, ratedSpeedRpm: 1_800,
        efficiencyPct: null, windingConnection: '12-lead, 2/3 pitch', enclosureProtection: 'IP23', coolingMethod: null,
        insulationClass: 'H', temperatureRiseClass: 'H', sourceIds: ['stamford-s5l1d-h4-2025'],
      },
    ],
    studyCases: [
      {
        ratingCaseId: '50hz-400v-h-rise', parameterBasis: 'not-stated', reactances: null, timeConstants: null,
        statorResistancePu: null, shortCircuitRatio: null, rotorInertiaKgM2: 11.6197, inertiaConstantSeconds: null,
        shortCircuitDecrementCurvePublished: true, neutralGroundingDataPublished: false, avrModelPublished: false,
        validatedRmsModelPublished: false, validatedEmtModelPublished: false, sourceIds: ['stamford-s5l1d-h4-2025'],
        notes: 'Exact sheet publishes saturated/unsaturated reactance and time-constant tables plus decrement curves; values require winding/rating-specific transcription review before study export. PMG excitation sustains 300% fault current for up to 10 s.',
      },
      {
        ratingCaseId: '60hz-480v-h-rise', parameterBasis: 'not-stated', reactances: null, timeConstants: null,
        statorResistancePu: null, shortCircuitRatio: null, rotorInertiaKgM2: 11.6197, inertiaConstantSeconds: null,
        shortCircuitDecrementCurvePublished: true, neutralGroundingDataPublished: false, avrModelPublished: false,
        validatedRmsModelPublished: false, validatedEmtModelPublished: false, sourceIds: ['stamford-s5l1d-h4-2025'],
        notes: 'Exact sheet publishes frequency-specific tables and decrement curves; do not reuse the 50 Hz per-unit row without transcription review.',
      },
    ],
    physicalReferences: [physical({
      id: 'shipping-crate', label: 'Shipping crate', scope: 'transport-envelope', lengthM: 1.6, widthM: 0.9, heightM: 1.15,
      massTonnes: 1.84, configurationScope: 'exact-product', coverageScope: 'per-product', includes: ['generator'],
      sourceIds: ['stamford-s5l1d-h4-2025'], limitation: 'Shipping crate only; not the coupled-machine GA, terminal-box envelope, maintenance pull space, or installed footprint.',
    })],
    dataGaps: ['Reviewed transcription of exact reactance/time-constant table', 'Selected AVR settings and limiter model', 'Neutral grounding design', 'Coupling/adaptor and terminal-box GA', 'Validated RMS/EMT model files'],
  },
  {
    schemaVersion: OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
    id: 'avk-dig140k6-60hz-4160v',
    manufacturer: 'STAMFORD | AvK',
    family: 'AvK DIG 140',
    model: 'DIG140k/6',
    variant: '60 Hz, 4.16 kV, six-pole synchronous generator',
    productStatus: 'legacy-current-support',
    identitySourceIds: ['avk-dig140k6-4160'],
    preferredRatingCaseId: '60hz-4160v',
    ratingCases: [{
      id: '60hz-4160v', label: '60 Hz, 4.16 kV continuous rating', ratedKva: 3_800, ratedVoltageKv: 4.16,
      frequencyHz: 60, phases: 3, ratedPowerFactor: 0.8, poles: 6, ratedSpeedRpm: 1_200, efficiencyPct: null,
      windingConnection: null, enclosureProtection: 'IP23', coolingMethod: null, insulationClass: null,
      temperatureRiseClass: null, sourceIds: ['avk-dig140k6-4160'], notes: 'Published 3,040 kWe at power factor 0.8; DECS100 excitation.',
    }],
    studyCases: [{
      ratingCaseId: '60hz-4160v', parameterBasis: 'saturated',
      reactances: { xdPu: 1.62, xPrimeDPu: 0.32, xDoublePrimeDPu: 0.18, xqPu: 0.88, xDoublePrimeQPu: 0.198, negativeSequencePu: 0.189, zeroSequencePu: 0.054, leakagePu: null, baseMva: 3.8 },
      timeConstants: { tPrimeDoSeconds: 2.7, tPrimeDSeconds: 0.48, tDoublePrimeDSeconds: 0.015, tDoublePrimeQSeconds: 0.03, armatureSeconds: 0.07 },
      statorResistancePu: null, shortCircuitRatio: null, rotorInertiaKgM2: 250, inertiaConstantSeconds: null,
      shortCircuitDecrementCurvePublished: true, neutralGroundingDataPublished: false, avrModelPublished: false,
      validatedRmsModelPublished: false, validatedEmtModelPublished: false, sourceIds: ['avk-dig140k6-4160'],
      notes: 'Unsaturated Xd=1.80 pu, Xdʺ=0.198 pu, Xq=0.90 pu, X2=0.208 pu, X0=0.059 pu. Additional open-circuit q-axis time constants remain in the source. Published Ikʺ=2,930 A, peak=7,459 A, sustained=1,582 A for maximum 10 s.',
    }],
    physicalReferences: [],
    dataGaps: ['Current order-status confirmation', 'Exact GA and terminal-box/coupling configuration', 'Neutral grounding design', 'DECS100 settings/model', 'Validated RMS/EMT model files'],
    notes: 'Official-hosted sheet revision is 2013; treat availability as supported/status-to-confirm rather than a current production promise.',
  },
  {
    schemaVersion: OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
    id: 'leroy-somer-lsa493-s4a-50hz-400v',
    manufacturer: 'Leroy-Somer / Nidec Power',
    family: 'LSA 49.3',
    model: 'LSA 49.3 S4A',
    variant: 'Four-pole AREP/PMG alternator at 400 V, 50 Hz',
    productStatus: 'current',
    identitySourceIds: ['leroy-lsa493-2020'],
    preferredRatingCaseId: '50hz-400v-h-continuous',
    ratingCases: [{
      id: '50hz-400v-h-continuous', label: '50 Hz, 400 V, Class-H continuous rating', ratedKva: 625,
      ratedVoltageKv: 0.4, frequencyHz: 50, phases: 3, ratedPowerFactor: null, poles: 4, ratedSpeedRpm: 1_500,
      efficiencyPct: null, windingConnection: '6-lead, 2/3 pitch', enclosureProtection: 'IP23', coolingMethod: null,
      insulationClass: 'H', temperatureRiseClass: 'H', sourceIds: ['leroy-lsa493-2020'],
      notes: 'AREP/PMG excitation with R449; exact duty/temperature-rise and voltage row retained.',
    }],
    studyCases: [{
      ratingCaseId: '50hz-400v-h-continuous', parameterBasis: 'mixed',
      reactances: { xdPu: 3.31, xPrimeDPu: 0.166, xDoublePrimeDPu: 0.133, xqPu: 1.99, xDoublePrimeQPu: 0.15, negativeSequencePu: 0.144, zeroSequencePu: 0.0035, leakagePu: null, baseMva: 0.625 },
      timeConstants: { tPrimeDoSeconds: 2.002, tPrimeDSeconds: 0.1, tDoublePrimeDSeconds: 0.01, tDoublePrimeQSeconds: null, armatureSeconds: 0.015 },
      statorResistancePu: null, shortCircuitRatio: 0.38, rotorInertiaKgM2: null, inertiaConstantSeconds: null,
      shortCircuitDecrementCurvePublished: true, neutralGroundingDataPublished: false, avrModelPublished: false,
      validatedRmsModelPublished: false, validatedEmtModelPublished: false, sourceIds: ['leroy-lsa493-2020'],
      notes: 'Xd is unsaturated; Xdʹ/Xdʺ are saturated, hence parameterBasis=mixed. Published motor-starting kVA is 1,350 and voltage-response time 500 ms; project AVR settings and validated model files are not public.',
    }],
    physicalReferences: [],
    dataGaps: ['Exact coupling/bearing and terminal-box GA', 'Rotor inertia or inertia constant for selected build', 'Neutral grounding design', 'R449 settings/limiter model', 'Validated RMS/EMT model files'],
  },
] satisfies readonly OemElectricalMachineProduct[];

/**
 * Hand-curated OEM facts. Unknown fields remain null and physical references
 * remain distinct from installed-operating envelopes. The register is frozen
 * by the public library facade before consumers can access it.
 */
export const RAW_OEM_GENERATION_SOURCE_REGISTER = {
  schemaVersion: OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
  compiledAt: '2026-07-20',
  sources,
  products,
  electricalMachines,
} satisfies OemGenerationSourceRegister;
